import { generateClient } from 'aws-amplify/api';
import { I18n } from 'aws-amplify/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import * as mutations from '@/graphql/mutations';
import { apiPut } from '@/modules/hooks/use-put';
import { ServiceType } from '@/types/serviceType';

type LocationState = google.maps.LatLngLiteral;

type UseVehicleLocationParams = {
  vehicleId: string | null;
  serviceType: ServiceType | undefined;
  storybookMode: boolean;
  onError: (message: string) => void;
};

/** 東京駅の座標。Storybook用のフォールバックのみに使用する。 */
const TOKYO_STATION: LocationState = { lat: 35.68105, lng: 139.76556 };

type UseVehicleLocationReturn = {
  /**
   * GPS取得済みの現在地。
   * 取得できない場合はフォールバック座標（東京駅）を返す。
   */
  location: LocationState;
  newLocationFlg: boolean;
};

export const useVehicleLocation = ({
  vehicleId,
  serviceType,
  storybookMode,
  onError,
}: UseVehicleLocationParams): UseVehicleLocationReturn => {
  // `mobipf-driver` と同様に、GPS取得不可でも現在地表示が消えないようフォールバック座標を持つ
  const [location, setLocation] = useState<LocationState>(TOKYO_STATION);
  const [oldLocation, setOldLocation] = useState<LocationState>(TOKYO_STATION);
  const [hasFirstCallExecuted, setHasFirstCallExecuted] = useState(true);
  const [newLocationFlg, setNewLocationFlg] = useState(false);

  const locationRef = useRef<LocationState>(location);
  const previousLocationRef = useRef<LocationState>({ lat: 0, lng: 0 });
  const locationPollTimerRef = useRef<number | NodeJS.Timeout | null>(null);
  /** 最後に登録を試行した位置。失敗時も更新し、同一位置の連続リトライを防ぐ */
  const lastAttemptedLocationRef = useRef<LocationState | null>(null);

  const hasLocationChanged = useCallback(
    (prev: LocationState, current: LocationState): boolean => prev.lat !== current.lat || prev.lng !== current.lng,
    [],
  );

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // GPS位置情報を1秒ごとにポーリング
  useEffect(() => {
    if (storybookMode) return;

    const timer = setInterval(() => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              lat: Number(position.coords.latitude.toFixed(6)),
              lng: Number(position.coords.longitude.toFixed(6)),
            });
            setNewLocationFlg(true);
          },
          () => {},
          { enableHighAccuracy: true, maximumAge: 0, timeout: Infinity },
        );
      }
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [storybookMode]);

  // 位置変化時にGraphQL mutationで車両位置を登録
  useEffect(() => {
    if (storybookMode) return;

    const currentLocation = { lat: location.lat, lng: location.lng };
    const lastAttempted = lastAttemptedLocationRef.current;
    const lastAttemptedMatches =
      lastAttempted && lastAttempted.lat === currentLocation.lat && lastAttempted.lng === currentLocation.lng;

    const registVehicleLocation = async () => {
      try {
        if (vehicleId !== null && vehicleId !== undefined && vehicleId !== '') {
          lastAttemptedLocationRef.current = currentLocation;
          const client = generateClient();
          const variables = {
            input: {
              vehicleId,
              latitude: location.lat,
              longitude: location.lng,
            },
          };
          const createRes = await client.graphql({ query: mutations.createVehicleLocation, variables });
          if (createRes?.errors) {
            onError(I18n.get(createRes.errors.toString()) as string);
          } else {
            // 正常終了時は最後の失敗位置をクリアし、任意の地点で再度登録できるようにする
            lastAttemptedLocationRef.current = null;
          }
          setOldLocation({ lat: location.lat, lng: location.lng });
        }
      } catch {
        /* 失敗時も lastAttemptedLocationRef は更新済みのため、同一位置の連続リトライを防ぐ */
      }
    };

    const hasChanged = location.lat !== oldLocation.lat || location.lng !== oldLocation.lng;
    const shouldAttempt = hasChanged && !lastAttemptedMatches;
    if (shouldAttempt) {
      void registVehicleLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, vehicleId, storybookMode, oldLocation?.lat, oldLocation?.lng, onError]);

  const googleVehicleApi = useCallback(
    async (lat: number, lng: number) => {
      const body = { latitude: lat, longitude: lng };
      try {
        apiPut(ApiNamePaths.googleVehicleApi.apiName, `${ApiNamePaths.googleVehicleApi.apiPath}/${vehicleId}`, {
          body,
        }).catch(() => {
          // 本APIでエラーが発生してもダイアログは表示されません（既存のドライバー位置情報更新と同様の設計方針を採用）。
        });
      } catch (error) {
        const err = error as { message: string };
        onError(I18n.get(err.message) as string);
      }
    },
    [onError, vehicleId],
  );

  // リアルタイムオンデマンド: 初回の位置送信
  useEffect(() => {
    if (storybookMode) return;
    if (serviceType !== ServiceType.RealtimeOnDemand) return;
    if (!(hasFirstCallExecuted && newLocationFlg)) return;

    const currentLocation = locationRef.current;
    void googleVehicleApi(currentLocation.lat, currentLocation.lng);
    previousLocationRef.current = currentLocation;
    setHasFirstCallExecuted(false);
    setNewLocationFlg(false);
  }, [googleVehicleApi, hasFirstCallExecuted, newLocationFlg, serviceType, storybookMode]);

  // リアルタイムオンデマンド: 20秒ごとにGoogle Vehicle APIへ位置を送信
  useEffect(() => {
    if (storybookMode) return;
    if (serviceType !== ServiceType.RealtimeOnDemand) return;

    locationPollTimerRef.current = setInterval(() => {
      const currentLocation = locationRef.current;
      if (hasLocationChanged(previousLocationRef.current, currentLocation)) {
        void googleVehicleApi(currentLocation.lat, currentLocation.lng);
        previousLocationRef.current = currentLocation;
      }
    }, 20000);

    return () => {
      if (locationPollTimerRef.current !== null) {
        clearInterval(locationPollTimerRef.current);
        locationPollTimerRef.current = null;
      }
    };
  }, [googleVehicleApi, serviceType, storybookMode, hasLocationChanged]);

  return { location, newLocationFlg };
};
